import React, { useEffect } from 'react';

import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { Translate } from 'react-localize-redux';

import SettingsList, { SettingsMenuItem } from '@fd/ui/List/SettingsList';

import useLookerDashboards from './customHooks/useLookerDashboards';
import { LookerDashboard } from './types/dashboard';
import { getReportsMenuItems } from './utils/getReportsMenuItems';

type ReportsMenuItemsListProps = {
  appId: string;
  reportType?: 'main' | 'rms'; // Filter by report type
  onError?: (error: unknown) => void;
};

const CircularProgressContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
}));

const ReportsMenuItemsList = (props: ReportsMenuItemsListProps) => {
  const { appId, reportType = 'main', onError } = props;
  const { data: rmsData, isLoading: isLoadingRMSCommonData } = useQueryRMSCommonDataHook(appId);
  const subscriptions = rmsData?.subscriptions || [];
  const menuSource = rmsData?.associations?.menuSource;

  // Fetch available dashboards from Looker API
  const {
    data: lookerDashboardsData = [],
    isLoading: isLoadingLookerDashboards,
    error: lookerDashboardsError,
  } = useLookerDashboards(appId);

  // Notify parent component of error
  useEffect(() => {
    lookerDashboardsError && onError?.(lookerDashboardsError);
  }, [lookerDashboardsError, onError]);

  const allMenuItems = getReportsMenuItems(appId, subscriptions, lookerDashboardsData, menuSource);

  // Filter menu items based on report type
  const menuItems = allMenuItems.filter((item) => {
    const isRmsReport = item.path.includes('/rms/');
    if (reportType === 'rms') {
      return isRmsReport;
    } else {
      return !isRmsReport; // main reports
    }
  });

  // Convert ReportMenuItem to SettingsMenuItem format
  const settingsMenuItems: SettingsMenuItem[] = menuItems.map((item) => ({
    title: item.title,
    path: item.path,
    icon: item.icon,
    subtitle: item.subtitle,
    hidden: item.hidden,
    dataFd: `All_reports_${item.title}`,
  }));

  const shouldShowAsLast = (item: SettingsMenuItem, index: number) => {
    // The last item should be the last one in the array
    return index === settingsMenuItems.length - 1;
  };

  if (isLoadingRMSCommonData || isLoadingLookerDashboards) {
    return (
      <CircularProgressContainer>
        <CircularProgress size={40} data-fd="reports-loading-spinner" />
      </CircularProgressContainer>
    );
  }

  return <SettingsList menuItems={settingsMenuItems} shouldShowAsLast={shouldShowAsLast} />;
};

export default ReportsMenuItemsList;
